import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  themeQuartz,
} from "ag-grid-community";
import { Box } from "@mui/material";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { dataGridColorParams } from "@platform/design-system";
import { getWrappingCellStyle } from "@platform/btx-grid-components";
import { formatUsd } from "@platform/formatting-utils/formatters";

import { listRateSchedules } from "../api/rate-schedules";
import {
  getBenefitPlanGroupPremiums,
  type BenefitPlanGroupPremiums,
} from "../api/benefit-plan-groups";
import { benefitPlanGroupQueryKeys } from "../hooks/useBenefitPlanGroups";
import { rateQueryKeys } from "../utils/query-keys";
import { isAlternatePlan, getPlanHeaderName } from "../utils/plan-helpers";
import {
  buildSummaryRowDataFromBackend,
  currentTotalKey,
  GROUP_TOTAL_KEY,
  priorRenewalKey,
  renewalKey,
} from "../utils/summary-rows";
import { displayedRenewalSchedule } from "../utils/rateSets";
import { renewalTooltipLines } from "../utils/renewal-tooltip";
import { scaleColumnDefWidths } from "../utils/scaleColumnDefWidths";
import {
  footnoteTargetForGroup,
  FOOTNOTE_ROW_KEY,
  withFootnoteAnchor,
} from "../utils/footnote-row";
import {
  buildRiderChargeRows,
  RIDER_CHARGE_FRACTION_DIGITS,
  riderChargeInputTestId,
  riderChargePatch,
  riderChargeRelationshipOf,
} from "../utils/rider-charge-rows";

import {
  DebouncedNumericInput,
  FocusableRateCellWrapper,
  FootnoteInput,
} from "./RatesGrid/renderers";
import { inputSx } from "./RatesGrid/renderers/constants";
import { PREMIUM_TOOLTIP_SX } from "./premiumTooltipSx";
import { RenewalPremiumCell } from "./RenewalPremiumCell";
import { RenewalPremiumHeader } from "./RenewalPremiumHeader";

import type {
  SummaryCellValue,
  SummaryRowData,
  GroupPremiumsLookup,
} from "../utils/summary-rows";
import type {
  FootnoteTarget,
  FootnotePlaceholderTarget,
} from "../utils/footnote-row";
import type { RiderChargeEditor } from "../utils/rider-charge-rows";
import type { AxiosInstance } from "axios";
import type {
  BenefitPlanGroup,
  CarrierBenefitPlanGroup,
} from "../types/models";
import type { ReactElement, ReactNode, RefObject } from "react";
import type { RateSchedule } from "../types";

// Matches RatesGrid's MIN_WIDTH exactly - scaleColumnDefWidths multiplies
// minWidth by columnWidthScale, so any mismatch between the two grids'
// base minWidth compounds per column as more plan columns are added.
const MIN_WIDTH = 450;

// useQueries returns a new result array every render; a stable `combine`
// makes React Query structurally share it, so the row data built from these
// queries (and AG Grid's refresh of every summary cell) only changes when a
// query does.
const combineRateScheduleData = (
  results: UseQueryResult<RateSchedule[]>[],
): (RateSchedule[] | undefined)[] => results.map((result) => result.data);

const combinePremiumQueries = (
  results: UseQueryResult<BenefitPlanGroupPremiums>[],
): { data: BenefitPlanGroupPremiums | undefined; isFetching: boolean }[] =>
  results.map(({ data, isFetching }) => ({ data, isFetching }));

// Shared defaults: a fresh `[]` or `{}` per render would rebuild every column.
const NO_ALIGNED_GRIDS: RefObject<AgGridReact>[] = [];
const NO_BENEFIT_PLAN_GROUPS: BenefitPlanGroup[] = [];
const NO_CARRIER_GROUPS: CarrierBenefitPlanGroup[] = [];
const NO_COLUMN_WIDTHS: Record<string, number> = {};

// Shared CSS constants to avoid duplication
const TRANSPARENT_IMPORTANT = "transparent !important";
const AG_BORDER_IMPORTANT =
  "1px solid var(--ag-border-color, #babfc7) !important";

// Custom tooltip: the default AG Grid one applies a heavy drop-shadow that
// renders as a black halo against the light grid. This is a plain card, lifted
// above the cursor so it sits over the cell rather than covering the next row.
const PremiumTooltip = (params: ITooltipParams): ReactElement | null => {
  const value = params.value as string | null | undefined;
  if (!value) return null;

  return (
    <Box sx={{ transform: "translateY(-100%)", ...PREMIUM_TOOLTIP_SX }}>
      {value}
    </Box>
  );
};

const hasRenewalCell = (
  data: SummaryRowData | undefined,
  planId: string,
): boolean =>
  !data?.[GROUP_TOTAL_KEY] && typeof data?.[renewalKey(planId)] === "number";

// Hover lines for a renewal total row, which carries the group's current total (and, when
// revised renewal is shown, the renewal total it supersedes) beside the shown value.
const renewalTotalTooltipLines = (
  data: SummaryRowData | undefined,
  planId: string,
  value: number,
): string[] | undefined => {
  const current = data?.[currentTotalKey(planId)];
  if (typeof current !== "number") return undefined;
  const prior = data?.[priorRenewalKey(planId)];
  return renewalTooltipLines(
    current,
    value,
    typeof prior === "number" ? prior : undefined,
  );
};

const numberOrUndefined = (value: SummaryCellValue | undefined) =>
  typeof value === "number" ? value : undefined;

// The split Current/Renewal cell draws its own tooltips, one per half, because the grid's
// is per cell. Every other premium, group totals included, uses the grid's.
const renderPremiumCell = (
  params: ICellRendererParams<SummaryRowData>,
): ReactNode => {
  const planId = params.colDef?.field;
  if (!planId || typeof params.value !== "number") {
    return params.valueFormatted ?? "";
  }

  const renewalPremium = numberOrUndefined(params.data?.[renewalKey(planId)]);
  if (renewalPremium !== undefined && !params.data?.[GROUP_TOTAL_KEY]) {
    return (
      <RenewalPremiumCell
        currentPremium={params.value}
        renewalPremium={renewalPremium}
        priorRenewalPremium={numberOrUndefined(
          params.data?.[priorRenewalKey(planId)],
        )}
      />
    );
  }

  return params.valueFormatted ?? "";
};

export interface SummaryGridProps {
  /** Refs to other AG Grid instances to align with (e.g., RatesGrid) */
  alignedGrids?: RefObject<AgGridReact>[];
  /** Benefit plan groups to display as columns (matches RatesGrid structure) */
  benefitPlanGroups?: BenefitPlanGroup[];
  /** Optional carrier-level grouping to mirror RatesGrid's carrier super-groups */
  carrierGroups?: CarrierBenefitPlanGroup[];
  /** Axios instance for API calls */
  axiosInstance?: AxiosInstance;
  /** Callback when grid is ready, exposes grid API for alignment */
  onGridReady?: (api: GridApi) => void;
  /** Scales plan-column widths (1 = default) to match the PlanDesigner width preset. */
  columnWidthScale?: number;
  /**
   * Column widths keyed by colId, sourced from the PlanEditor grid. Applied
   * directly to plan columns so SummaryGrid tracks PlanEditor widths without
   * relying solely on alignedGrids colId matching (mirrors RatesGrid).
   */
  columnWidths?: Record<string, number>;
  /**
   * When provided, appends an editable "Footnotes" row at the bottom — one
   * merged, editable cell per plan group (spanning that group's plan columns).
   * Called with the footnote's owning group and new text when the analyst
   * edits it. Omit to hide the row.
   */
  onFootnoteChange?: (target: FootnoteTarget, footnote: string) => void;
  /**
   * Label shown in the Footnotes row's pinned left cell. One per LOC plan
   * group (the grid is scoped to one), so it lives beside the row rather than
   * inside any carrier's column. Empty falls back to a "Footnotes" placeholder.
   */
  footnoteHeaderLabel?: string;
  /**
   * Called with the new label when the analyst edits the Footnotes row's
   * pinned left cell. Omit to render that cell read-only.
   */
  onFootnoteHeaderLabelChange?: (label: string) => void;
  /**
   * Lookup from a placeholder plan id to the keys needed to materialize its
   * "awaiting quote" plan group (mirrors RatesGrid's prop of the same name).
   * A footnote committed on an unmaterialized group has no persisted id to
   * save against, so its footnote target falls back to this instead.
   */
  placeholderMaterializeByPlanId?: Map<string, FootnotePlaceholderTarget>;
  /**
   * When provided, adds Employee, Spouse and Child(ren) Rider Charge rows
   * above the premiums, one cell per plan, editable where the editor allows.
   * Omit to hide the rows.
   */
  riderChargeEditor?: RiderChargeEditor;
}

/**
 * SummaryGrid component that aligns with PlanDesigner and RatesGrid columns
 *
 * This component uses AG Grid's aligned grids feature to ensure columns
 * line up with the grids above it. Shows monthly and annual cost summaries
 * per benefit plan.
 *
 * Column structure mirrors RatesGrid exactly:
 * - One column group per benefit plan group
 * - One sub-column per benefit plan within each group
 */
export const SummaryGrid = forwardRef<AgGridReact, SummaryGridProps>(
  (
    {
      alignedGrids = NO_ALIGNED_GRIDS,
      benefitPlanGroups = NO_BENEFIT_PLAN_GROUPS,
      carrierGroups = NO_CARRIER_GROUPS,
      axiosInstance,
      onGridReady: onGridReadyProp,
      columnWidthScale = 1,
      columnWidths = NO_COLUMN_WIDTHS,
      onFootnoteChange,
      footnoteHeaderLabel,
      onFootnoteHeaderLabelChange,
      placeholderMaterializeByPlanId,
      riderChargeEditor,
    },
    ref,
  ) => {
    const gridRef = useRef<AgGridReact>(null);

    // Expose the grid ref to parent via forwardRef
    useImperativeHandle(ref, () => gridRef.current as AgGridReact);

    // Note: gridApi is stored for potential future use
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_gridApi, setGridApi] = useState<GridApi | null>(null);

    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        setGridApi(params.api);
        onGridReadyProp?.(params.api);
      },
      [onGridReadyProp],
    );

    // Flatten carrier-grouped input to a plain group list, matching RatesGrid.
    const allBenefitPlanGroups = useMemo(
      () =>
        carrierGroups.length > 0
          ? carrierGroups.flatMap(
              (carrierGroup) => carrierGroup.benefitPlanGroups,
            )
          : benefitPlanGroups,
      [benefitPlanGroups, carrierGroups],
    );

    // Collect all plan IDs for fetching rate schedules
    const planIds = useMemo(
      () =>
        allBenefitPlanGroups.flatMap((group) =>
          group.benefitPlans.map((plan) => plan.id),
        ),
      [allBenefitPlanGroups],
    );

    // Callers (e.g. the Quotes SBS grid) sometimes already embed each plan's
    // rate schedule directly (proposal plans, and the synthetic Requested/
    // Incumbent column).
    const planById = useMemo(() => {
      const map = new Map<string, BenefitPlanGroup["benefitPlans"][number]>();
      allBenefitPlanGroups.forEach((group) => {
        group.benefitPlans.forEach((plan) => map.set(plan.id, plan));
      });
      return map;
    }, [allBenefitPlanGroups]);

    // Premiums come from the backend (ADR-026), so nothing here fetches rate
    // schedules. These disabled observers never run their queryFn: they only
    // read the cache slot that useResolvedRateSchedules and the rate-save
    // mutations write to, so the summary can reflect an edit the moment it
    // lands (see the fallback in buildSummaryRowDataFromBackend).
    const rateScheduleQueries = useQueries({
      queries: planIds.map((planId) => ({
        queryKey: rateQueryKeys.schedules(planId),
        queryFn: () => listRateSchedules(planId, axiosInstance!),
        enabled: false,
      })),
      combine: combineRateScheduleData,
    });

    // Create a map of planId -> rate schedules for easy lookup, preferring the
    // query cache over each plan's embedded rateSchedule: the cache reflects the
    // latest edit immediately, while the embedded value is a snapshot from
    // whenever the parent's payload was last fetched and only used before
    // anything has seeded that cache slot.
    const rateSchedulesByPlanId = useMemo(() => {
      const map = new Map<string, RateSchedule[]>();
      planIds.forEach((planId, index) => {
        const data = rateScheduleQueries[index];
        if (data) {
          map.set(planId, data);
          return;
        }
        const embedded = planById.get(planId)?.rateSchedule;
        if (embedded) {
          map.set(planId, embedded);
        }
      });
      return map;
    }, [planIds, planById, rateScheduleQueries]);

    // Fetch the per-group premium rollups separately from the (heavy,
    // editable) benefit-plan-groups payload. An
    // inline rate save invalidates `benefitPlanGroupQueryKeys.premiums()`, which
    // refreshes just these rows without remounting the rates grid (XTRM-2024).
    // This only applies to real btx benefit-plan-groups (e.g. the Build/
    // Workbench rates grid, and the Incumbent/Requested column) — proposal
    // plan groups (quoted or placeholder, e.g. Quotes/Present) come from a
    // different table (`isProposalGroup`) and already carry their premiums
    // embedded on every read (proposalPlanGroupToRatesBenefitPlanGroup), so
    // `group.id` there isn't a valid benefit-plan-group id and would just 404.
    const groupIds = useMemo(
      () => allBenefitPlanGroups.map((group) => group.id),
      [allBenefitPlanGroups],
    );

    // Cells created before a plan's renewal rates arrived keep AG Grid's own tooltip bound to
    // them, which then shows beside the tooltips the split cell draws itself. Redrawing the
    // premium rows when the set of plans with renewal rates changes drops those stale
    // bindings. The Footnotes and rider-charge rows are left alone: redrawing them would
    // reset a draft being typed.
    const renewalPlanIds = useMemo(
      () =>
        planIds
          .filter((planId) =>
            displayedRenewalSchedule(planById.get(planId)?.rateSchedule),
          )
          .join(","),
      [planIds, planById],
    );
    useEffect(() => {
      const api = gridRef.current?.api;
      if (!api) return;
      const premiumRowNodes: IRowNode<SummaryRowData>[] = [];
      api.forEachNode((node) => {
        const isEditableRow =
          Boolean(node.data?.[FOOTNOTE_ROW_KEY]) ||
          riderChargeRelationshipOf(node.data) !== undefined;
        if (!isEditableRow) premiumRowNodes.push(node);
      });
      api.redrawRows({ rowNodes: premiumRowNodes });
    }, [renewalPlanIds]);

    const premiumQueries = useQueries({
      queries: allBenefitPlanGroups.map((group) => ({
        queryKey: benefitPlanGroupQueryKeys.premium(group.id),
        queryFn: () => getBenefitPlanGroupPremiums(group.id, axiosInstance!),
        // A failure here (e.g. a group id that turns out not to be a real
        // benefit-plan-group) is not transient — retrying just repeats the
        // same error against the same id.
        retry: false,
        staleTime: 30_000,
        enabled: !!axiosInstance && !!group.id && !group.isProposalGroup,
      })),
      combine: combinePremiumQueries,
    });

    const premiumsByGroupId = useMemo(() => {
      const map = new Map<string, GroupPremiumsLookup>();
      groupIds.forEach((groupId, index) => {
        const query = premiumQueries[index];
        if (!query?.data || query.isFetching) return;
        const data = query.data;
        map.set(groupId, {
          totalMonthlyPremium: data.totalMonthlyPremium,
          totalAnnualPremium: data.totalAnnualPremium,
          planPremiums: new Map(
            data.benefitPlans.map((plan) => [
              plan.id,
              {
                monthlyPremium: plan.monthlyPremium,
                annualPremium: plan.annualPremium,
              },
            ]),
          ),
        });
      });
      return map;
    }, [groupIds, premiumQueries]);

    // Returns explicit width props sourced from PlanEditor when available.
    // For Dental PPO plans, PlanEditor has two sub-columns (inNetwork +
    // outOfNetwork) — sum them to get the full width. Mirrors RatesGrid.
    // minWidth is pinned to the same value: the column's default minWidth
    // (MIN_WIDTH × scale) can round 1px larger than PlanEditor's two summed
    // sub-columns, and AG Grid would otherwise clamp the explicit width back up.
    const resolveColumnWidth = useCallback(
      (
        colId: string,
      ):
        | { width: number; minWidth: number; flex: 0 }
        | Record<string, never> => {
        if (columnWidths[colId] !== undefined) {
          const width = columnWidths[colId];
          return { width, minWidth: width, flex: 0 };
        }
        const inNet = columnWidths[`${colId}.inNetwork`];
        const outNet = columnWidths[`${colId}.outOfNetwork`];
        if (inNet !== undefined && outNet !== undefined) {
          const width = inNet + outNet;
          return { width, minWidth: width, flex: 0 };
        }
        return {};
      },
      [columnWidths],
    );

    // Renders the row-category label, except on the footnote row, where this
    // cell is the editable per-LOC-plan-group footnote header.
    const SummaryTypeCellRenderer = useCallback(
      (params: ICellRendererParams<SummaryRowData>) => {
        if (params.data?.[FOOTNOTE_ROW_KEY]) {
          if (onFootnoteHeaderLabelChange) {
            return (
              <FocusableRateCellWrapper eGridCell={params.eGridCell}>
                <FootnoteInput
                  serverValue={footnoteHeaderLabel ?? ""}
                  placeholder="Footnotes"
                  ariaLabel="Footnotes row header"
                  commitOnEnter
                  onCommit={onFootnoteHeaderLabelChange}
                />
              </FocusableRateCellWrapper>
            );
          }
          return footnoteHeaderLabel?.trim()
            ? footnoteHeaderLabel
            : "Footnotes";
        }

        return (params.value as string) ?? "";
      },
      [footnoteHeaderLabel, onFootnoteHeaderLabelChange],
    );

    // Define the left "Summary" column to match PlanDesigner's benefits column width
    const leftColumn: ColDef<SummaryRowData> = useMemo(
      () => ({
        field: "summaryType",
        headerName: "Summary",
        pinned: "left",
        flex: 0, // Don't flex - use fixed width
        width: 200,
        // Matches the PlanEditor benefits column's minWidth so aligned-grid
        // width sync is not clamped when that column is dragged narrower.
        minWidth: 100,
        suppressMovable: true,
        lockPosition: "left",
        resizable: false,
        editable: false,
        suppressHeaderMenuButton: true,
        autoHeight: true,
        wrapText: true,
        cellRenderer: SummaryTypeCellRenderer,
        cellStyle: (params) =>
          getWrappingCellStyle(Boolean(params.data?.[FOOTNOTE_ROW_KEY])),
        // Same hover/focus affordance the footnote content cells get, so the
        // header reads as editable too; the -header- class then realigns it to
        // sit like the row labels above it.
        cellClassRules: {
          "rates-footnote-cell": (params) =>
            Boolean(params.data?.[FOOTNOTE_ROW_KEY]),
          "rates-footnote-header-cell": (params) =>
            Boolean(params.data?.[FOOTNOTE_ROW_KEY]),
        },
      }),
      [SummaryTypeCellRenderer],
    );

    // Renders either the formatted premium value or, on the footnote row, an
    // editable footnote input — mirrors RatesGrid's RatesCellRenderer pattern.
    const SummaryCellRenderer = useCallback(
      (
        params: ICellRendererParams<SummaryRowData> & {
          footnoteTarget?: FootnoteTarget;
        },
      ) => {
        const { eGridCell } = params;

        if (params.data?.[FOOTNOTE_ROW_KEY]) {
          const footnoteValue = (params.value as string) ?? "";
          const target = params.footnoteTarget;
          if (onFootnoteChange && target) {
            return (
              <FocusableRateCellWrapper eGridCell={eGridCell}>
                <FootnoteInput
                  serverValue={footnoteValue}
                  onCommit={(value) => onFootnoteChange(target, value)}
                />
              </FocusableRateCellWrapper>
            );
          }
          return footnoteValue;
        }

        const riderChargeRelationship = riderChargeRelationshipOf(params.data);
        const planId = params.colDef?.field;
        if (riderChargeRelationship && planId) {
          if (!riderChargeEditor?.isEditable(planId)) {
            return params.valueFormatted ?? "";
          }
          const charge: unknown = params.value;
          // Held in a variable: the input attributes' type has no data-*
          // keys, though the DOM takes them.
          const riderChargeInputProps = {
            "aria-label": `${String(params.data?.["summaryType"])}, ${params.colDef?.headerName ?? planId}`,
            "data-testid": riderChargeInputTestId(
              riderChargeRelationship,
              planId,
            ),
          };
          return (
            <FocusableRateCellWrapper eGridCell={eGridCell}>
              <DebouncedNumericInput
                serverValue={
                  typeof charge === "number"
                    ? charge.toFixed(RIDER_CHARGE_FRACTION_DIGITS)
                    : ""
                }
                onCommit={(value) => {
                  riderChargeEditor.onChange(
                    planId,
                    riderChargePatch(
                      riderChargeRelationship,
                      value === "" ? null : Number(value),
                    ),
                  );
                }}
                allowDecimals
                isCurrency
                sx={inputSx}
                inputProps={riderChargeInputProps}
              />
            </FocusableRateCellWrapper>
          );
        }

        return renderPremiumCell(params);
      },
      [onFootnoteChange, riderChargeEditor],
    );

    // Create a sub-column for each benefit plan in a group, matching RatesGrid's
    // plan-column structure exactly so alignedGrids line up.
    const buildPlanColumns = useCallback(
      (
        group: BenefitPlanGroup,
        // Carrier-grouped mode keys colId off plan.id (matching PlanEditor):
        // identifiers are RFP plan labels ("CI-1-1") repeated across carriers,
        // so they collide in a side-by-side grid.
        keyColumnsByPlanId: boolean,
      ): ColDef<SummaryRowData>[] => {
        const realPlanCount = group.benefitPlans.filter(
          (plan) => !isAlternatePlan(plan),
        ).length;
        const groupHasTotal = realPlanCount > 1;
        const firstPlanId = group.benefitPlans[0]?.id;
        const placeholderTarget = firstPlanId
          ? placeholderMaterializeByPlanId?.get(firstPlanId)
          : undefined;
        const footnoteTarget = footnoteTargetForGroup(group, placeholderTarget);

        return group.benefitPlans.map((plan, planIndex) => {
          const renewal = displayedRenewalSchedule(plan.rateSchedule);
          // A plan with renewal rates labels its premium columns instead of naming itself.
          const renewalHeader = renewal
            ? {
                headerComponent: RenewalPremiumHeader,
                headerComponentParams: {
                  renewalLabel:
                    renewal.category === "revisedRenewal"
                      ? "Rev. Ren."
                      : "Renewal",
                },
                headerClass: "renewal-premium-header",
              }
            : {};
          return {
            ...renewalHeader,
            // Matches PlanEditor's colId in carrier-grouped mode, PlanDesigner's
            // on the flat Build grid — see keyColumnsByPlanId above.
            colId: keyColumnsByPlanId ? plan.id : plan.identifier,
            field: plan.id,
            headerName: getPlanHeaderName(plan),
            flex: 1,
            minWidth: MIN_WIDTH, // Consistent width for aligned grid sync with PlanDesigner/RatesGrid
            resizable: false,
            editable: false,
            suppressHeaderMenuButton: true,
            autoHeight: true,
            wrapText: true,
            cellStyle: (params) =>
              params.data?.[FOOTNOTE_ROW_KEY]
                ? getWrappingCellStyle(true)
                : { textAlign: "right" },
            // The split Current/Renewal cell fills the cell edge to edge; the padding
            // reset lives in the grid's sx because its `.ag-cell` rule is !important.
            cellClassRules: {
              "renewal-premium-split-cell": (params) =>
                hasRenewalCell(params.data, plan.id),
            },
            cellRenderer: SummaryCellRenderer,
            cellRendererParams: {
              footnoteTarget,
            },
            // On the group-total rows, span the group's first plan column
            // across the whole group so the total reads as a single value.
            colSpan: (params) => {
              const isGroupTotalRow = Boolean(params.data?.[GROUP_TOTAL_KEY]);
              if (isGroupTotalRow && groupHasTotal && planIndex === 0) {
                return group.benefitPlans.length;
              }
              return 1;
            },
            valueFormatter: (params) => {
              if (params.data?.[FOOTNOTE_ROW_KEY] || params.value == null)
                return "";
              const fractionDigits = riderChargeRelationshipOf(params.data)
                ? RIDER_CHARGE_FRACTION_DIGITS
                : 0;
              return formatUsd(params.value as number, fractionDigits);
            },
            // Split Current/Renewal cells draw their own tooltips in the cell renderer,
            // because the grid's tooltip is per cell and the split cell needs one per half.
            // The getter is always set and returns "" for those cells rather than being
            // omitted: AG Grid keeps a property from the previous colDef when an update
            // leaves it out, so a column that gains renewal rates after load (or a plan
            // group switch) would otherwise keep showing its old grid tooltip too.
            tooltipValueGetter: (params: ITooltipParams<SummaryRowData>) => {
              if (
                hasRenewalCell(params.data, plan.id) ||
                params.data?.[FOOTNOTE_ROW_KEY] ||
                riderChargeRelationshipOf(params.data) ||
                params.value == null
              ) {
                return "";
              }
              const totalLines = renewalTotalTooltipLines(
                params.data,
                plan.id,
                params.value as number,
              );
              if (totalLines) return totalLines.join("\n");
              // The premium to the cent (PB-717).
              return formatUsd(params.value as number, 2);
            },
            tooltipComponent: PremiumTooltip,
          };
        });
      },
      [SummaryCellRenderer, placeholderMaterializeByPlanId],
    );

    // Create a column group for each benefit plan group with sub-columns for each benefit plan
    // This mirrors the exact structure in RatesGrid
    const groupColumns: ColDef<SummaryRowData>[] = useMemo(
      () =>
        benefitPlanGroups.map((group, index) => ({
          groupId: `group-${group.id}`,
          headerName:
            group.nickname || `Plan Group ${group.position || index + 1}`,
          marryChildren: true, // Keep children columns together
          children: withFootnoteAnchor(buildPlanColumns(group, false)),
        })),
      [benefitPlanGroups, buildPlanColumns],
    );

    // Carrier-grouped columns mirror RatesGrid's carrierGroupedColumns: one
    // header group per carrier, containing that carrier's plan groups.
    const carrierGroupedColumns: ColDef<SummaryRowData>[] = useMemo(
      () =>
        carrierGroups.map((carrierGroup) => ({
          headerName: carrierGroup.carrierName,
          groupId: `carrier-${carrierGroup.groupKey ?? carrierGroup.carrierId}`,
          marryChildren: false,
          children: carrierGroup.benefitPlanGroups.map((group, index) => ({
            groupId: `group-${group.id}`,
            headerName:
              group.nickname || `Plan Group ${group.position || index + 1}`,
            marryChildren: true,
            children: withFootnoteAnchor(buildPlanColumns(group, true)),
          })),
        })),
      [carrierGroups, buildPlanColumns],
    );

    // Apply PlanEditor column widths after scaling so the already-rendered pixel
    // widths from PlanEditor are not scaled a second time by scaleColumnDefWidths.
    const applyWidthOverrides = useCallback(
      (defs: ColDef<SummaryRowData>[]): ColDef<SummaryRowData>[] =>
        defs.map((def) => {
          if ("children" in def && def.children) {
            return {
              ...def,
              children: applyWidthOverrides(
                def.children as ColDef<SummaryRowData>[],
              ),
            };
          }
          if (!def.colId) return def;
          // Match on colId first, then fall back to field (the plan id) for
          // columns whose colId does not match how PlanEditor keys its own.
          let override = resolveColumnWidth(def.colId);
          if (Object.keys(override).length === 0 && def.field) {
            override = resolveColumnWidth(def.field);
          }
          return Object.keys(override).length > 0
            ? { ...def, ...override }
            : def;
        }),
      [resolveColumnWidth],
    );

    // Define columns that will align with plan group columns
    const columnDefs = useMemo(
      () =>
        applyWidthOverrides(
          scaleColumnDefWidths(
            [
              leftColumn,
              ...(carrierGroups.length > 0
                ? carrierGroupedColumns
                : groupColumns),
            ],
            columnWidthScale,
          ) as ColDef<SummaryRowData>[],
        ),
      [
        leftColumn,
        groupColumns,
        carrierGroupedColumns,
        carrierGroups,
        columnWidthScale,
        applyWidthOverrides,
      ],
    );

    const defaultColDef: ColDef<SummaryRowData> = useMemo(
      () => ({
        flex: 1,
        minWidth: MIN_WIDTH, // Consistent width for aligned grid sync with PlanDesigner/RatesGrid
        resizable: false,
        sortable: false,
        filter: false,
        editable: false,
      }),
      [],
    );

    const gridOptions: GridOptions<SummaryRowData> = useMemo(
      () => ({
        theme: themeQuartz.withParams(dataGridColorParams),
        domLayout: "autoHeight",
        // Matches RatesGrid: without this, the two grids can reserve different
        // horizontal scrollbar space, so `flex: 1` columns divide up a
        // slightly different total width and drift further apart as more
        // plan columns are added.
        suppressHorizontalScroll: true,
        groupHeaderHeight: 0, // Hide benefit plan group row
        headerHeight: 24,
        rowHeight: 28,
        suppressNoRowsOverlay: true,
        tooltipShowDelay: 300, // AG Grid defaults to ~2s; too slow for a hover lookup.
        tabIndex: 3, // Tab order after RatesGrid
        // computedRowData rebuilds every row as a brand-new object on each
        // proposals refetch (the Quotes tab polls every 10s while awaiting
        // carrier quotes). Without a stable id, AG Grid can't match those new
        // objects to the previous row nodes and remounts every cell renderer
        // instead of updating props in place — which resets the Footnotes
        // row's FootnoteInput mid-type, discarding the analyst's draft.
        // summaryType is a fixed category label (or "" for the footnote row),
        // independent of the row's actual values, so it's a safe row id.
        // data is typed as always-present, but AG Grid can call this with it
        // undefined (e.g. PlanEditor's gridOptions.ts guards the same way).
        getRowId: (params) => String(params.data?.["summaryType"] ?? ""),
      }),
      [],
    );

    // Compute row data for monthly/annual premiums plus plan-group-level totals.
    // Fields are keyed by plan.id to match the column structure exactly.
    const computedRowData = useMemo(() => {
      const premiumRows = buildSummaryRowDataFromBackend(
        allBenefitPlanGroups,
        premiumsByGroupId,
        rateSchedulesByPlanId,
      );
      // Rider charges sit below the rates and above the premiums.
      const rows = riderChargeEditor
        ? [...buildRiderChargeRows(allBenefitPlanGroups), ...premiumRows]
        : premiumRows;

      if (!onFootnoteChange) return rows;

      // The left-column label is the analyst-editable footnote header, supplied
      // by SummaryTypeCellRenderer rather than stored on the row.
      const footnoteRow: SummaryRowData = {
        summaryType: "",
        [FOOTNOTE_ROW_KEY]: true,
      };
      // One footnote per plan group, anchored on that group's first plan
      // column. Only groups with a persisted savable owner (proposal plan
      // group for quoted carriers, RFP benefit plan group for the incumbent/
      // requested column) are savable, so skip the rest (synthetic
      // placeholders).
      allBenefitPlanGroups.forEach((group) => {
        if (!footnoteTargetForGroup(group)) return;
        const firstPlanId = group.benefitPlans[0]?.id;
        if (firstPlanId) footnoteRow[firstPlanId] = group.footnote ?? "";
      });

      return [...rows, footnoteRow];
    }, [
      allBenefitPlanGroups,
      rateSchedulesByPlanId,
      premiumsByGroupId,
      onFootnoteChange,
      riderChargeEditor,
    ]);

    return (
      <Box
        className="summary-grid"
        sx={{
          display: "block",
          width: "100%",
          margin: 0,
          padding: 0,
          "& .ag-root-wrapper": {
            borderRadius: 0, // Remove border radius from AG Grid wrapper
            minHeight: "unset", // Override any AG Grid minimum height
          },
          "& .ag-body-viewport": {
            minHeight: "unset", // Ensure viewport doesn't have minimum height
          },
          "& .ag-center-cols-container": {
            minHeight: "unset !important", // Override AG Grid's default 150px min-height
          },
          "& .ag-center-cols-viewport": {
            minHeight: "unset !important", // Override AG Grid's default 150px min-height
          },
          // Remove all hover highlights
          "& .ag-row:hover, & .ag-row-hover, & .ag-row.ag-row-hover": {
            backgroundColor: TRANSPARENT_IMPORTANT,
            "--ag-row-hover-color": TRANSPARENT_IMPORTANT,
          },
          "& .ag-cell:hover, & .ag-cell-hover, & .ag-cell.ag-cell-range-selected":
            {
              backgroundColor: TRANSPARENT_IMPORTANT,
            },
          "&": {
            "--ag-row-hover-color": "transparent",
            "--ag-selected-row-background-color": "transparent",
            "--ag-range-selection-background-color": "transparent",
          },
          // Reduce header cell padding and add borders to match RatesGrid/PlanEditor
          "& .ag-header-cell": {
            padding: "0 8px !important",
            borderTop: AG_BORDER_IMPORTANT,
            borderRight: AG_BORDER_IMPORTANT,
            borderBottom: AG_BORDER_IMPORTANT,
          },
          // The renewal premium header lays out its own labels edge to edge, like the cells.
          "& .ag-header-cell.renewal-premium-header": {
            padding: "0 !important",
          },
          // The extra class outranks the `.ag-cell` padding rule below.
          "& .ag-cell.renewal-premium-split-cell": {
            padding: "0 !important",
          },
          // First header cell gets left border to close off the row
          "& .ag-header-cell:first-of-type": {
            borderLeft: AG_BORDER_IMPORTANT,
          },
          // Remove cell padding so child content is flush with borders, matching
          // RatesGrid's box model exactly — otherwise cells with no value (e.g.
          // alternate plans) fall back to the AG Grid theme's default inner
          // padding and no longer line up with RatesGrid/PlanEditor above.
          "& .ag-cell": {
            padding: "0 8px !important",
            borderRight: AG_BORDER_IMPORTANT,
            borderBottom: AG_BORDER_IMPORTANT,
          },
          // First column gets left border
          '& .ag-cell:first-child, & .ag-cell[col-id="summaryType"]': {
            borderLeft: AG_BORDER_IMPORTANT,
          },
          // Ensure cell wrapper and content expand to fill full cell width
          "& .ag-cell-wrapper": {
            width: "100%",
          },
          "& .ag-cell-value": {
            width: "100%",
          },
          "& .ag-cell.rates-footnote-cell": {
            display: "flex",
            alignItems: "flex-start",
          },
          "& .ag-cell.rates-footnote-cell:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04) !important",
          },
          "& .ag-cell.rates-footnote-cell:focus-within": {
            backgroundColor: "rgba(25, 118, 210, 0.08) !important",
          },
          // Align the footnote header with the row labels above it ("Monthly
          // Premium" etc.): vertically centred rather than top-aligned, and
          // indented once — the cell already supplies the 8px, so the
          // textarea's own left padding would double it.
          "& .ag-cell.rates-footnote-header-cell": {
            alignItems: "center",
          },
          "& .ag-cell.rates-footnote-header-cell textarea": {
            paddingLeft: 0,
          },
          // Kill the drop-shadow the AG Grid theme puts on the tooltip popup
          // wrapper (PremiumTooltip supplies its own look).
          "& .ag-tooltip-custom": {
            boxShadow: "none",
            filter: "none",
          },
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={computedRowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          suppressMovableColumns
          alignedGrids={alignedGrids}
        />
      </Box>
    );
  },
);

SummaryGrid.displayName = "SummaryGrid";

export default SummaryGrid;
